import React, { useState, useEffect, useRef } from "react";
import { Typography } from "antd";
import TranslateText from "../../resuableComponents/TranslateText";
import "./errorPage.css";

const isNonProd = process.env.ENVIRONMENT !== "PRODUCTION";
const SUPPORT_EMAIL = "checkout-support@boxpay.tech";

const safeStringify = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export default function ErrorPage({ imgUrl, heading, subHeading, errorDetails, showContactSupport = false }) {

  const safeErrorDetails = safeStringify(errorDetails);
  const [showEmailOptions, setShowEmailOptions] = useState(false);
  const [copied, setCopied] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowEmailOptions(false);
      }
    };
    if (showEmailOptions) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showEmailOptions]);

  const subject = "Checkout Technical Error - Support Required";
  const truncatedError = safeErrorDetails && safeErrorDetails.length > 800
    ? safeErrorDetails.substring(0, 800) + `\n...(truncated, copy full error from screen)`
    : safeErrorDetails;
  const body = `Hi BoxPay Support,\n\nI encountered a technical error on the checkout page.\n\nError Details:\n${truncatedError || "No error details available"}\n\nPage URL: ${window.location.href}\nTimestamp: ${new Date().toISOString()}\n\nPlease help resolve this issue.\n\nThanks`;

  const encodedSubject = encodeURIComponent(subject);
  const encodedBody = encodeURIComponent(body);

  const emailOptions = [
    {
      label: "Gmail",
      icon: "https://ssl.gstatic.com/ui/v1/icons/mail/rfr/gmail.ico",
      url: `https://mail.google.com/mail/?view=cm&fs=1&to=${SUPPORT_EMAIL}&su=${encodedSubject}&body=${encodedBody}`,
    },
    {
      label: "Outlook",
      icon: "https://www.google.com/s2/favicons?domain=outlook.live.com&sz=16",
      url: `https://outlook.live.com/mail/0/deeplink/compose?to=${SUPPORT_EMAIL}&subject=${encodedSubject}&body=${encodedBody}`,
    },
    {
      label: "Default Mail App",
      icon: null,
      url: `mailto:${SUPPORT_EMAIL}?subject=${encodedSubject}&body=${encodedBody}`,
    },
  ];

  const handleCopy = () => {
    navigator.clipboard.writeText(`To: ${SUPPORT_EMAIL}\nSubject: ${subject}\n\n${body}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };



  return (
    <div className="errorMainDiv">
      <img src={imgUrl} alt="img" />
      <Typography className="errormainHeading">{heading}</Typography>
      <Typography className="errorSubHeading">{subHeading}</Typography>

      {showContactSupport && <Typography className="errorRetryText">
        <TranslateText label={"FOR_FURTHER_ASSISTANCE"}>For further assistance</TranslateText>
      </Typography>}

      {showContactSupport && <div className="contactSupportWrapper" ref={dropdownRef}>
        <button
          className="contactSupportBtn"
          onClick={() => setShowEmailOptions((prev) => !prev)}
        >
          Contact Support
        </button>

        {showEmailOptions && (
          <div className="emailOptionsDropdown">
            {emailOptions.map((option) => (
              <a
                key={option.label}
                href={option.url}
                target="_blank"
                rel="noreferrer"
                className="emailOption"
              >
                {option.icon && <img src={option.icon} alt={option.label} className="emailOptionIcon" />}
                {option.label}
              </a>
            ))}
            <button className="emailOption copyOption" onClick={handleCopy}>
              📋 {copied ? "Copied!" : "Copy error to clipboard"}
            </button>
          </div>
        )}
      </div>}

      {isNonProd && safeErrorDetails && (
        <div className="errorDetailsContainer">
          <pre className="errorDetailsBlock">{safeErrorDetails}</pre>
        </div>
      )}
    </div>
  );
}
